import {
  addMonthsToMonth,
  diffDays,
  EVENT_COST_CATEGORY,
  monthOf,
  recordIds,
  type PaymentMethod,
} from '@dairy/shared';
import { live, type DomainCategory, type DomainEvent, type DomainExpense, type DomainPayment, type DomainSale } from './types';

/** D-9: split a total over n items — integer division, the remainder on the first item. */
export function splitEvenly(totalSantim: number, n: number): number[] {
  if (n <= 0) return [];
  const each = Math.floor(totalSantim / n);
  const rest = totalSantim - each * n;
  return Array.from({ length: n }, (_, i) => (i === 0 ? each + rest : each));
}

/** D-9: milk sale amount in santim = floor((ml × price per liter + 500) / 1000), i.e. round half up. */
export function milkSaleAmount(ml: number, unitPriceSantim: number): number {
  return Math.floor((ml * unitPriceSantim + 500) / 1000);
}

export type AgeBucket = '0-7' | '8-30' | '31-60' | '60+';

export function ageBucket(ageDays: number): AgeBucket {
  if (ageDays <= 7) return '0-7';
  if (ageDays <= 30) return '8-30';
  if (ageDays <= 60) return '31-60';
  return '60+';
}

export interface UnpaidSale {
  saleId: string;
  date: string;
  amountSantim: number;
  unpaidSantim: number;
  ageDays: number;
  bucket: AgeBucket;
  overdue: boolean;
}

export interface BuyerLedger {
  balanceSantim: number;
  salesSantim: number;
  paymentsSantim: number;
  unpaid: UnpaidSale[];
  overdueSantim: number;
  buckets: Record<AgeBucket, number>;
  oldestUnpaidAgeDays: number | null;
}

function saleOrder(a: DomainSale, b: DomainSale): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const ca = a.createdAt ?? a.clientCreatedAt ?? '';
  const cb = b.createdAt ?? b.clientCreatedAt ?? '';
  if (ca !== cb) return ca < cb ? -1 : 1;
  return a.id < b.id ? -1 : 1;
}

/**
 * D-9: balance = sales − payments (negative = credit); payments pay off the oldest sales first (FIFO);
 * unpaid amounts with their age, bucket and overdue flag, as of `asOf` (sales and payments dated after it are ignored).
 */
export function buyerLedger(
  sales: readonly DomainSale[],
  payments: readonly DomainPayment[],
  asOf: string,
  overdueDays: number,
): BuyerLedger {
  const ss = live(sales)
    .filter((s) => s.date <= asOf && !!s.buyerId)
    .sort(saleOrder);
  const ps = live(payments).filter((p) => p.date <= asOf);
  const salesTotal = ss.reduce((a, s) => a + s.amountSantim, 0);
  const paid = ps.reduce((a, p) => a + p.amountSantim, 0);
  let remaining = paid;
  const unpaid: UnpaidSale[] = [];
  const buckets: Record<AgeBucket, number> = { '0-7': 0, '8-30': 0, '31-60': 0, '60+': 0 };
  let overdue = 0;
  for (const s of ss) {
    const applied = Math.min(remaining, s.amountSantim);
    remaining -= applied;
    const left = s.amountSantim - applied;
    if (left <= 0) continue;
    const ageDays = diffDays(asOf, s.date);
    const bucket = ageBucket(ageDays);
    const isOverdue = ageDays > overdueDays;
    unpaid.push({ saleId: s.id, date: s.date, amountSantim: s.amountSantim, unpaidSantim: left, ageDays, bucket, overdue: isOverdue });
    buckets[bucket] += left;
    if (isOverdue) overdue += left;
  }
  return {
    balanceSantim: salesTotal - paid,
    salesSantim: salesTotal,
    paymentsSantim: paid,
    unpaid,
    overdueSantim: overdue,
    buckets,
    oldestUnpaidAgeDays: unpaid.length ? unpaid[0]!.ageDays : null,
  };
}

export interface StatementLine {
  date: string;
  kind: 'SALE' | 'PAYMENT';
  id: string;
  amountSantim: number;
  runningSantim: number;
  description?: string;
}

export interface BuyerStatement {
  from: string;
  to: string;
  openingSantim: number;
  lines: StatementLine[];
  closingSantim: number;
}

/** IN-4: opening balance (everything before `from`), lines in date order with a running balance, closing balance. */
export function buyerStatement(
  sales: readonly DomainSale[],
  payments: readonly DomainPayment[],
  from: string,
  to: string,
): BuyerStatement {
  const ss = live(sales).filter((s) => !!s.buyerId);
  const ps = live(payments);
  let opening = 0;
  for (const s of ss) if (s.date < from) opening += s.amountSantim;
  for (const p of ps) if (p.date < from) opening -= p.amountSantim;
  type Row = { date: string; kind: 'SALE' | 'PAYMENT'; id: string; amount: number; created: string; milkMl?: number | null };
  const rows: Row[] = [
    ...ss
      .filter((s) => s.date >= from && s.date <= to)
      .map((s) => ({ date: s.date, kind: 'SALE' as const, id: s.id, amount: s.amountSantim, created: s.createdAt ?? s.clientCreatedAt ?? '', milkMl: s.milkMl })),
    ...ps
      .filter((p) => p.date >= from && p.date <= to)
      .map((p) => ({ date: p.date, kind: 'PAYMENT' as const, id: p.id, amount: p.amountSantim, created: p.createdAt ?? p.clientCreatedAt ?? '' })),
  ].sort((a, b) =>
    a.date !== b.date ? (a.date < b.date ? -1 : 1) : a.kind !== b.kind ? (a.kind === 'SALE' ? -1 : 1) : a.created < b.created ? -1 : 1,
  );
  let running = opening;
  const lines: StatementLine[] = rows.map((r) => {
    running += r.kind === 'SALE' ? r.amount : -r.amount;
    return { date: r.date, kind: r.kind, id: r.id, amountSantim: r.amount, runningSantim: running };
  });
  return { from, to, openingSantim: opening, lines, closingSantim: running };
}

export type ReconcileFlag = 'OK' | 'UNACCOUNTED' | 'OVERSOLD';

/**
 * IN-6 / D-8: unaccounted = saleable − sold. Flag UNACCOUNTED when the gap is larger than `threshold` of saleable
 * milk AND larger than 1 L; OVERSOLD when more was sold than recorded as saleable.
 */
export function reconcileMilkDay(
  saleableMl: number,
  soldMl: number,
  threshold: number,
): { unaccountedMl: number; flag: ReconcileFlag } {
  const unaccountedMl = saleableMl - soldMl;
  if (soldMl > 0 && soldMl > saleableMl) return { unaccountedMl, flag: 'OVERSOLD' };
  const permille = Math.round(threshold * 1000);
  if (unaccountedMl * 1000 > permille * saleableMl && unaccountedMl > 1000) {
    return { unaccountedMl, flag: 'UNACCOUNTED' };
  }
  return { unaccountedMl, flag: 'OK' };
}

/** D-10 accrual: floor(A/n) in each month m … m+n−1, plus the remainder in month m. */
export function allocateExpense(expense: Pick<DomainExpense, 'date' | 'amountSantim' | 'coversMonths'>): Array<{
  month: string;
  santim: number;
}> {
  const n = Math.max(1, expense.coversMonths || 1);
  const start = monthOf(expense.date);
  return splitEvenly(expense.amountSantim, n).map((santim, i) => ({ month: addMonthsToMonth(start, i), santim }));
}

export interface ExpenseDraft {
  id: string;
  date: string;
  categoryId: string;
  amountSantim: number;
  coversMonths: number;
  method: PaymentMethod;
  animalId: string;
  sourceEventId: string;
  description: string | null;
}

/** EX-3: the expense an event's cost creates, or null when there is no cost (or no mapping). */
export function expenseForEvent(
  event: Pick<DomainEvent, 'id' | 'type' | 'date' | 'animalId' | 'costSantim' | 'deletedAt'>,
  categoriesBySystemKey: ReadonlyMap<string, Pick<DomainCategory, 'id'>> | Record<string, Pick<DomainCategory, 'id'>>,
): ExpenseDraft | null {
  if (event.deletedAt) return null;
  if (!event.costSantim || event.costSantim <= 0) return null;
  const key = EVENT_COST_CATEGORY[event.type];
  if (!key) return null;
  const cat =
    categoriesBySystemKey instanceof Map
      ? categoriesBySystemKey.get(key)
      : (categoriesBySystemKey as Record<string, Pick<DomainCategory, 'id'>>)[key];
  if (!cat) return null;
  return {
    id: recordIds.expenseFromEvent(event.id),
    date: event.date,
    categoryId: cat.id,
    amountSantim: event.costSantim,
    coversMonths: 1,
    method: 'CASH',
    animalId: event.animalId,
    sourceEventId: event.id,
    description: null,
  };
}

export interface CategoryAmount {
  categoryId: string;
  santim: number;
}

export interface MonthTotals {
  month: string;
  accrual: {
    operatingSantim: number;
    capitalSantim: number;
    feedSantim: number;
    byCategory: CategoryAmount[];
    byGroup: CategoryAmount[];
  };
  cash: {
    totalSantim: number;
    operatingSantim: number;
    capitalSantim: number;
    byCategory: CategoryAmount[];
  };
}

function groupOf(cat: DomainCategory | undefined, byId: ReadonlyMap<string, DomainCategory>): DomainCategory | undefined {
  if (!cat) return undefined;
  return cat.parentId ? (byId.get(cat.parentId) ?? cat) : cat;
}

function addTo(map: Map<string, number>, key: string, v: number) {
  map.set(key, (map.get(key) ?? 0) + v);
}

const toList = (m: Map<string, number>): CategoryAmount[] =>
  [...m.entries()]
    .map(([categoryId, santim]) => ({ categoryId, santim }))
    .sort((a, b) => b.santim - a.santim || (a.categoryId < b.categoryId ? -1 : 1));

/** D-10: a month's expense totals — accrual (cost, profit) and cash (cash flow); operating vs capital. */
export function monthTotals(
  expenses: readonly DomainExpense[],
  categories: readonly DomainCategory[],
  month: string,
): MonthTotals {
  const byId = new Map(categories.map((c) => [c.id, c]));
  let operating = 0;
  let capital = 0;
  let feed = 0;
  const accCat = new Map<string, number>();
  const accGroup = new Map<string, number>();
  let cashTotal = 0;
  let cashOp = 0;
  let cashCap = 0;
  const cashCat = new Map<string, number>();
  for (const e of live(expenses)) {
    const cat = byId.get(e.categoryId);
    const costType = cat?.costType ?? groupOf(cat, byId)?.costType ?? 'OPERATING';
    for (const a of allocateExpense(e)) {
      if (a.month !== month) continue;
      if (costType === 'CAPITAL') capital += a.santim;
      else operating += a.santim;
      if (cat?.isFeed) feed += a.santim;
      addTo(accCat, e.categoryId, a.santim);
      addTo(accGroup, groupOf(cat, byId)?.id ?? e.categoryId, a.santim);
    }
    if (monthOf(e.date) === month) {
      cashTotal += e.amountSantim;
      if (costType === 'CAPITAL') cashCap += e.amountSantim;
      else cashOp += e.amountSantim;
      addTo(cashCat, e.categoryId, e.amountSantim);
    }
  }
  return {
    month,
    accrual: { operatingSantim: operating, capitalSantim: capital, feedSantim: feed, byCategory: toList(accCat), byGroup: toList(accGroup) },
    cash: { totalSantim: cashTotal, operatingSantim: cashOp, capitalSantim: cashCap, byCategory: toList(cashCat) },
  };
}
